import { useDepartmentText } from 'model/DepartmentTerminology';
import {
  Accordion,
  Box,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Progress,
  rem,
  Select,
  SimpleGrid,
  Stack,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDeselect, IconRestore, IconSelectAll } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import { useFileManager } from 'api/file-manager/file-manager-context';
import Button from 'components/Button';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import { OrganizationSelect } from 'components/selects/OrganizationSelect';
import Toast from 'components/Toast';
import P2Regular from 'components/typography/P2Regular';
import P3Medium from 'components/typography/P3Medium';
import { SYSTEM_EXPORT_PARALLELISM } from 'env';
import panic from 'errors/panic';
import { noop } from 'lodash';
import { DEVICE_FILE_TYPE_NAME_MAP, DeviceFileType } from 'model/DeviceFileType';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collectRevisionAttachments } from 'utils/revision-attachments';
import ParallelTaskRunner from 'utils/parallel-task-runner';
import UserTable from 'components/tables/data/user/UserTable';
import DepartmentTable from 'components/tables/data/department/DepartmentTable';
import DeviceTable from 'components/tables/data/device/DeviceTable';
import RevisionPlanTable from 'components/tables/data/revision-plan/RevisionPlanTable';
import FaultTable from 'components/tables/data/fault/FaultTable';
import RevisionTable from 'components/tables/data/revision/RevisionTable';
import { IDataTablePublic } from 'components/tables/DataTable';
import { nanoid } from 'nanoid';
import toBase64 from 'utils/to-base64';
import {
  DEFAULT_EXPORT_LIMITS,
  EXPORT_MAPPING_NAME,
  ExportEntry,
  ExportPathError,
  planSystemExport,
} from 'components/tables/data/system-export/system-export-paths';

import {
  defaultExportSettings,
  EXPORT_PATH_PARTS,
  ExportPathPart,
  ExportDisplayStyle,
  exportSettingsError,
  readExportSettings,
  saveExportSettings,
} from 'components/tables/data/system-export/system-export-settings';

interface SystemExportModalProps {
  opened: boolean;
  onClose: () => void;
  onExport?: (systemExportId: number, fileId: string) => void;
}

const MIME_TYPE_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const BATCH_SIZE = 50;

const DOCUMENT_OPTIONS = [
  { key: 'revisions', label: 'Revízne správy' },
  { key: 'revisionAttachments', label: 'Prílohy revíznych správ' },
  { key: 'forms', label: 'Formuláre' },
  { key: 'formAttachments', label: 'Prílohy formulárov' },
];
const DEVICE_OPTIONS = Object.entries(DEVICE_FILE_TYPE_NAME_MAP)
  .filter(([type]) => type !== 'department')
  .map(([type, label]) => ({ key: `device:${type}`, label }));
const WORKBOOK_KEYS = ['users', 'departments', 'devices', 'plans', 'faults', 'revisions', 'forms'];
const ALL_CONTENT_KEYS = [
  ...DOCUMENT_OPTIONS.map(({ key }) => key),
  ...DEVICE_OPTIONS.map(({ key }) => key),
  'departmentFiles',
  ...WORKBOOK_KEYS.map((key) => `workbook:${key}`),
];
const DEFAULT_CONTENT: Record<string, boolean> = Object.fromEntries(ALL_CONTENT_KEYS.map((key) => [key, true]));

/** Provides an awaitable callback ref for tables which mount only while the export is running. */
function useAwaitableDataTable() {
  const current = useRef<IDataTablePublic | null>(null);
  const waiters = useRef<Array<(table: IDataTablePublic) => void>>([]);
  const ref = useCallback((table: IDataTablePublic | null) => {
    current.current = table;
    if (table) waiters.current.splice(0).forEach((resolve) => resolve(table));
  }, []);
  const get = useCallback(
    () =>
      current.current
        ? Promise.resolve(current.current)
        : new Promise<IDataTablePublic>((resolve) => waiters.current.push(resolve)),
    []
  );

  return { ref, get };
}

/**
 * Modal for performing system export.
 */
export default function SystemExportModal({ opened, onClose, onExport = noop }: SystemExportModalProps) {
  const { buildZip, getFileMetadata, uploadFile } = useFileManager();
  const departmentText = useDepartmentText();
  const { getAction } = useApi();
  const [loading, { open: startLoading, close: stopLoading }] = useDisclosure(false);
  const [organizationId, setOrganizationId] = useState<string | null>('');
  const [step, setStep] = useState(0);
  const [steps, setSteps] = useState(0);
  const [message, setMessage] = useState('');
  const [settings, setSettings] = useState(() => readExportSettings(DEFAULT_CONTENT));
  const { content, parts, pathLength } = settings;
  /** Update the persisted content selection without changing naming preferences. */
  const setContent = (
    value: Record<string, boolean> | ((previous: Record<string, boolean>) => Record<string, boolean>)
  ) =>
    setSettings((previous) => ({
      ...previous,
      content: typeof value === 'function' ? value(previous.content) : value,
    }));
  const hasContent = Object.values(content).some(Boolean);
  const [advanced, setAdvanced] = useState<string | null>(null);
  const [pathError, setPathError] = useState<string | null>(null);
  /** Explicitly restore every advanced preference. */
  const resetSettings = () => {
    setSettings(defaultExportSettings(DEFAULT_CONTENT));
    setPathError(null);
  };
  useEffect(() => saveExportSettings(settings), [settings]);
  useEffect(() => {
    setPathError(null);
    setAdvanced(null);
  }, [opened]);
  const limits = { ...DEFAULT_EXPORT_LIMITS, pathLength: Number(pathLength) };
  const limitsError = exportSettingsError(settings);
  const handleClose = useCallback(() => {
    if (!loading) {
      setPathError(null);
      setAdvanced(null);
      onClose();
    }
  }, [loading, onClose]);
  const partLabels: Record<ExportPathPart, string> = {
    organization: 'Organizácia / archív',
    department: departmentText('Položka úrovne', 'Stredisko'),
    type: 'Typ zariadenia',
    subtype: 'Podtyp zariadenia',
    device: 'Zariadenie',
    revision: 'Revízia / formulár',
    file: 'Súbor',
  };

  const userTable = useAwaitableDataTable();
  const departmentTable = useAwaitableDataTable();
  const deviceTable = useAwaitableDataTable();
  const revisionPlanTable = useAwaitableDataTable();
  const faultTable = useAwaitableDataTable();
  const revisionTable = useAwaitableDataTable();
  const formTable = useAwaitableDataTable();

  const workbooks = [
    { key: 'users', name: 'Používatelia.xlsx', message: 'Sťahujem zoznam používateľov', table: userTable },
    {
      key: 'departments',
      name: departmentText('Položky úrovne.xlsx', 'Strediská.xlsx'),
      message: departmentText('Sťahujem zoznam položiek úrovne', 'Sťahujem zoznam stredísk'),
      table: departmentTable,
    },
    { key: 'devices', name: 'Zariadenia.xlsx', message: 'Sťahujem zoznam zariadení', table: deviceTable },
    { key: 'plans', name: 'Termíny.xlsx', message: 'Sťahujem zoznam termínov', table: revisionPlanTable },
    { key: 'faults', name: 'Nedostatky.xlsx', message: 'Sťahujem zoznam nedostatkov', table: faultTable },
    { key: 'revisions', name: 'Revízie.xlsx', message: 'Sťahujem zoznam revízií', table: revisionTable },
    { key: 'forms', name: 'Formuláre.xlsx', message: 'Sťahujem zoznam formulárov', table: formTable },
  ];
  const selectedWorkbooks = workbooks.filter(({ key }) => content[`workbook:${key}`]);
  const contentGroups = [
    { label: 'Revízie a formuláre', options: DOCUMENT_OPTIONS },
    { label: 'Súbory zariadení', options: DEVICE_OPTIONS },
    {
      label: departmentText('Súbory položiek úrovne', 'Súbory stredísk'),
      options: [
        {
          key: 'departmentFiles',
          label: departmentText('Zdieľané súbory položiek úrovne', 'Zdieľané súbory stredísk'),
        },
      ],
    },
    { label: 'Prehľady XLSX', options: workbooks.map(({ key, name }) => ({ key: `workbook:${key}`, label: name })) },
  ];

  /** Handles system export. */
  const handleExport = async () => {
    try {
      if (!organizationId || limitsError || !hasContent) {
        return;
      }

      setPathError(null);
      startLoading();
      setMessage('Sťahujem zoznam zariadení a revíznych správ');

      const listDevices = getAction('DeviceList');
      const listRevisions = getAction('RevisionList');
      const getDevice = getAction('DeviceGet');
      const getRevision = getAction('RevisionGet');
      const getOrganization = getAction('OrganizationGet');
      const createSystemExport = getAction('SystemExportCreate');
      const getDepartmentFiles = getAction('SystemExportDepartmentFiles');

      const runner = new ParallelTaskRunner(SYSTEM_EXPORT_PARALLELISM);
      const entries: ExportEntry[] = [];
      const workbookIds = new Map<string, string>();

      const [{ organizationName }, devices, revisions, departmentFiles] = await Promise.all([
        getOrganization({ parameters: { organizationId } }),
        DEVICE_OPTIONS.some(({ key }) => content[key])
          ? listDevices({ query: { filters: { 'organizationId.eq': organizationId } } })
          : Promise.resolve([]),
        DOCUMENT_OPTIONS.some(({ key }) => content[key])
          ? listRevisions({ query: { filters: { 'customerOrganizationId.eq': organizationId } } })
          : Promise.resolve([]),
        content.departmentFiles ? getDepartmentFiles({ parameters: { organizationId } }) : Promise.resolve([]),
      ]);

      setMessage('Sťahujem súbory zariadení a revízne správy');

      // Export device files
      for (const { deviceId, department } of devices) {
        runner.add(async () => {
          const device = await getDevice({ parameters: { deviceId: String(deviceId) } });

          for (const { fileId, fileType, fileName } of device.files ?? []) {
            if (fileType !== 'department' && content[`device:${fileType}`]) {
              const metadata = await getFileMetadata({ fileId });

              const fileNameParts = fileName.split('.');

              const fileNameWithoutExtension =
                fileNameParts.length > 1 ? fileNameParts.slice(0, -1).join('.') : fileName;

              const fileNameWithExtension = `${fileNameWithoutExtension}${metadata.extension}`;

              entries.push({
                fileId,
                fileName: fileNameWithExtension,
                directories: [
                  {
                    id: `department:${department.departmentId}`,
                    name: department.departmentName,
                    part: 'department' as const,
                    displayId: String(department.departmentId),
                  },
                  { id: 'devices', name: 'Zariadenia' },
                  {
                    id: `type:${device.deviceType.deviceTypeId}`,
                    name: device.deviceType.deviceTypeName,
                    part: 'type',
                    displayId: String(device.deviceType.deviceTypeId),
                  },
                  {
                    id: `subtype:${device.deviceSubtype.deviceTypeId}`,
                    name: device.deviceSubtype.deviceTypeName,
                    part: 'subtype',
                    displayId: String(device.deviceSubtype.deviceTypeId),
                  },
                  {
                    id: `device:${deviceId}`,
                    name: device.deviceName,
                    prefix: `${deviceId} - `,
                    part: 'device',
                    displayId: String(deviceId),
                  },
                  { id: fileType, name: DEVICE_FILE_TYPE_NAME_MAP[fileType as DeviceFileType] },
                ],
              });
            }
          }

          setStep((prev) => prev + 1);
        });
      }

      // Include the same attachment sources as detail, even before a PDF has been generated.
      for (const { revisionId, revisionName, fileId, signedFileId, department, kind } of revisions) {
        const includeOutput = content[kind === 'revision' ? 'revisions' : 'forms'];
        const includeAttachments = content[kind === 'revision' ? 'revisionAttachments' : 'formAttachments'];
        if (!includeOutput && !includeAttachments) continue;
        runner.add(async () => {
          const directories = [
            {
              id: `department:${department.departmentId}`,
              name: department.departmentName,
              part: 'department' as const,
              displayId: String(department.departmentId),
            },
            { id: kind, name: kind === 'revision' ? 'RS' : 'F' },
            {
              id: `revision:${revisionId}`,
              name: revisionName,
              prefix: `${revisionId} - `,
              part: 'revision' as const,
              displayId: String(revisionId),
            },
          ];
          const exportFileId = includeOutput ? signedFileId || fileId : null;
          if (exportFileId) {
            const metadata = await getFileMetadata({ fileId: exportFileId });
            entries.push({ fileId: exportFileId, fileName: metadata.basename, directories });
          }
          if (includeAttachments) {
            const revision = await getRevision({ parameters: { revisionId: String(revisionId) } });
            const revisionData = JSON.parse(revision.revisionData || 'null');
            if (revisionData) {
              const attachments = collectRevisionAttachments(
                revisionData,
                JSON.parse(revision.revisionTemplate?.formScheme ?? 'null')
              );
              // One binary per document folder, including a source repeated in several fields.
              const exported = new Set([exportFileId]);
              for (const { fileId: attachmentId, fileName } of attachments) {
                if (exported.has(attachmentId)) continue;
                await getFileMetadata({ fileId: attachmentId });
                entries.push({ fileId: attachmentId, fileName, directories });
                exported.add(attachmentId);
              }
            }
          }
          setStep((prev) => prev + 1);
        });
      }

      const sharedFiles = new Set<string>();
      for (const { departmentId, departmentName, fileId, fileName } of departmentFiles) {
        const key = `${departmentId}:${fileId}`;
        if (sharedFiles.has(key)) continue;
        sharedFiles.add(key);
        runner.add(async () => {
          const metadata = await getFileMetadata({ fileId });
          const extensionIndex = fileName.lastIndexOf('.');
          const fileNameWithoutExtension = extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName;
          entries.push({
            fileId,
            fileName: `${fileNameWithoutExtension}${metadata.extension ?? ''}`,
            directories: [
              {
                id: `department:${departmentId}`,
                name: departmentName,
                part: 'department',
                displayId: String(departmentId),
              },
              { id: 'shared', name: 'Zdieľané súbory' },
            ],
          });
          setStep((prev) => prev + 1);
        });
      }
      setSteps(runner.length + selectedWorkbooks.length + 1);
      await runner.run();
      const plan = planSystemExport({
        organizationName,
        organizationId,
        parts,
        date: new Date().toISOString().slice(0, 10),
        limits,
        entries: [
          ...entries,
          ...selectedWorkbooks.map(({ key, name }) => ({
            fileId: `workbook:${name}`,
            namingId: key,
            fileName: name,
            directories: [],
          })),
        ],
      });

      for (const { name, message: workbookMessage, table } of selectedWorkbooks) {
        setMessage(workbookMessage);
        const xlsx = await (await table.get()).getDataAsXlsx();
        const fileId = await uploadFile({
          fileName: `${nanoid()}.xlsx`,
          contents: `data:${MIME_TYPE_XLSX};base64,${xlsx}`,
        });
        workbookIds.set(`workbook:${name}`, fileId);
        setStep((prev) => prev + 1);
      }
      setMessage('Vytváram ZIP archív');
      const mappingId = await uploadFile({
        fileName: EXPORT_MAPPING_NAME,
        contents: await toBase64(new Blob([plan.mappingCsv], { type: 'text/csv;charset=utf-8' })),
      });
      setStep((prev) => prev + 1);
      const files = plan.files.map(({ fileId, path }) => ({ fileId: workbookIds.get(fileId) || fileId, path }));
      files.push({ fileId: mappingId, path: plan.mappingPath });
      const zipSteps = Math.ceil(files.length / BATCH_SIZE) + 2;
      setSteps((prev) => prev + zipSteps);
      const zipFileId = await buildZip({
        fileName: plan.archiveName,
        files,
        batchSize: BATCH_SIZE,
        onProgress: () => setStep((prev) => prev + 1),
      });

      setMessage('Vytváram záznam exportu');
      const { systemExportId } = await createSystemExport({
        payload: { organizationId: Number(organizationId), fileId: zipFileId },
      });
      setOrganizationId(null);
      onExport(systemExportId, zipFileId);
      onClose();
    } catch (error: any) {
      if (error instanceof ExportPathError) {
        setPathError(error.message);
        setAdvanced('limits');
      } else {
        setOrganizationId(null);
        panic(error);
        onClose();
      }
    } finally {
      stopLoading();
      setStep(0);
      setSteps(0);
      setMessage('');
    }
  };

  const dataFilters = useMemo(
    () => ({ 'organization.organizationId': { filter: organizationId === null ? [] : [String(organizationId)] } }),
    [organizationId]
  );

  return (
    <Modal
      title="Systémový export"
      opened={opened}
      onClose={handleClose}
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      closeButtonProps={{ loading }}
      size={650}
      centered
    >
      {loading && (
        <Stack display="none">
          {content['workbook:users'] && (
            <UserTable
              ref={userTable.ref}
              suppressSaveProfiles
              suppressProfileChangedNotification
              initialFilters={dataFilters}
            />
          )}
          {content['workbook:departments'] && (
            <DepartmentTable
              ref={departmentTable.ref}
              suppressSaveProfiles
              suppressProfileChangedNotification
              initialFilters={dataFilters}
            />
          )}
          {content['workbook:devices'] && (
            <DeviceTable
              ref={deviceTable.ref}
              suppressSaveProfiles
              suppressProfileChangedNotification
              initialFilters={dataFilters}
            />
          )}
          {content['workbook:plans'] && (
            <RevisionPlanTable
              ref={revisionPlanTable.ref}
              suppressSaveProfiles
              suppressProfileChangedNotification
              initialFilters={dataFilters}
            />
          )}
          {content['workbook:faults'] && (
            <FaultTable
              ref={faultTable.ref}
              suppressSaveProfiles
              suppressProfileChangedNotification
              initialFilters={dataFilters}
            />
          )}
          {content['workbook:revisions'] && (
            <RevisionTable
              ref={revisionTable.ref}
              suppressSaveProfiles
              suppressProfileChangedNotification
              initialFilters={dataFilters}
              kind="revision"
            />
          )}
          {content['workbook:forms'] && (
            <RevisionTable
              ref={formTable.ref}
              suppressSaveProfiles
              suppressProfileChangedNotification
              initialFilters={dataFilters}
              kind="form"
            />
          )}
        </Stack>
      )}
      {loading ? (
        <Stack spacing={4} pt={16}>
          <P2Regular>{message} &hellip;</P2Regular>
          <Group spacing={8} noWrap>
            <Progress w="100%" value={(step / steps) * 100} />
            <P3Medium>
              {step}&nbsp;/&nbsp;{steps}
            </P3Medium>
          </Group>
        </Stack>
      ) : (
        <Stack spacing={16} pt={16}>
          <Toast
            type="info"
            message="Systémový export umožňuje exportovať všetky zariadenia a revízne správy zvolenej organizácie."
            withCloseButton={false}
          />
          <OrganizationSelect
            label="Vyberte organizáciu"
            value={organizationId}
            onChange={setOrganizationId}
            autoSelectSingleResult
            showDiscarded={false}
          />
          <Accordion
            value={advanced}
            onChange={setAdvanced}
            chevronSize={18}
            styles={{
              item: { border: 0 },
              control: { padding: 0, '&:hover': { backgroundColor: 'transparent' } },
              label: { padding: 0 },
              content: { padding: '12px 0 0' },
            }}
          >
            <Accordion.Item value="limits">
              <Accordion.Control aria-label="Pokročilé nastavenia">
                <Box fz="sm" fw={500} c="gray.9">
                  Pokročilé nastavenia
                </Box>
                <Box fz="xs" c="gray.6">
                  Obsah a názvy archívu. Nastavenia sa ukladajú v tomto prehliadači.
                </Box>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack spacing={16} bg="red.0" p={12} sx={{ borderRadius: rem(4) }}>
                  {contentGroups.map(({ label, options }) => (
                    <Stack key={label} spacing={8} role="group" aria-label={label}>
                      <P3Medium>{label}</P3Medium>
                      <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'xs', cols: 1 }]} spacing={8}>
                        {options.map(({ key, label: optionLabel }) => (
                          <Checkbox
                            key={key}
                            label={optionLabel}
                            checked={!!content[key]}
                            onChange={(event) => {
                              const checked = event.currentTarget.checked;
                              setContent((previous) => ({ ...previous, [key]: checked }));
                              setPathError(null);
                            }}
                          />
                        ))}
                      </SimpleGrid>
                    </Stack>
                  ))}
                  <Stack spacing={12} role="group" aria-label="Časti cesty">
                    <Group spacing={4} noWrap>
                      <P3Medium>Časti cesty</P3Medium>
                      <QuestionMarkTooltip
                        withinPortal={false}
                        multiline
                        width={300}
                        buttonLabel="Pomoc: Časti cesty"
                        label="Priečinky kategórií majú pevné názvy vrátane RS a F. Limity zahŕňajú prípony a dátum archívu; vybrané ID sa neskracuje."
                      />
                    </Group>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) minmax(112px, 1.2fr) 72px',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      <P3Medium c="gray.6">Časť cesty</P3Medium>
                      <P3Medium c="gray.6">Štýl zobrazenia</P3Medium>
                      <P3Medium c="gray.6">Max. dĺžka</P3Medium>
                      {EXPORT_PATH_PARTS.map((part) => (
                        <Fragment key={part}>
                          <P3Medium sx={{ overflowWrap: 'anywhere' }}>{partLabels[part]}</P3Medium>
                          <Select
                            withinPortal={false}
                            size="sm"
                            aria-label={`${partLabels[part]}: štýl zobrazenia`}
                            value={parts[part].style}
                            data={[
                              { value: 'id', label: 'Iba ID' },
                              { value: 'label', label: 'Iba názov' },
                              { value: 'id-label', label: 'ID + názov' },
                            ]}
                            onChange={(style: ExportDisplayStyle) => {
                              setSettings((previous) => ({
                                ...previous,
                                parts: { ...previous.parts, [part]: { ...previous.parts[part], style } },
                              }));
                              setPathError(null);
                            }}
                          />
                          <NumberInput
                            size="sm"
                            aria-label={`${partLabels[part]}: maximálna dĺžka`}
                            value={parts[part].maxLength}
                            onChange={(maxLength) => {
                              setSettings((previous) => ({
                                ...previous,
                                parts: { ...previous.parts, [part]: { ...previous.parts[part], maxLength } },
                              }));
                              setPathError(null);
                            }}
                            min={10}
                            max={255}
                            precision={0}
                          />
                        </Fragment>
                      ))}
                    </Box>
                  </Stack>
                  <NumberInput
                    size="sm"
                    aria-label="Maximálna dĺžka cesty v ZIP"
                    labelProps={{ labelElement: 'div' }}
                    label={
                      <Group spacing={4} noWrap>
                        <span>Maximálna dĺžka cesty v ZIP</span>
                        <QuestionMarkTooltip
                          withinPortal={false}
                          multiline
                          width={330}
                          buttonLabel="Pomoc: Dĺžka cesty vo Windows"
                          label="Windows započítava aj priečinok, do ktorého ZIP rozbalíte. Vyššie limity môžu znemožniť rozbalenie alebo otvorenie súborov."
                        />
                      </Group>
                    }
                    value={pathLength}
                    onChange={(value) => {
                      setSettings((previous) => ({ ...previous, pathLength: value }));
                      setPathError(null);
                    }}
                    min={40}
                    max={1024}
                    precision={0}
                  />
                  <Group spacing={12}>
                    <Button
                      variant="subtle-gray"
                      size="sm"
                      onClick={() => setContent(DEFAULT_CONTENT)}
                      leftIcon={<IconSelectAll size={16} stroke={1.5} />}
                    >
                      Vybrať všetky súbory
                    </Button>
                    <Button
                      variant="subtle-gray"
                      size="sm"
                      onClick={() => setContent(Object.fromEntries(ALL_CONTENT_KEYS.map((key) => [key, false])))}
                      leftIcon={<IconDeselect size={16} stroke={1.5} />}
                    >
                      Zrušiť výber súborov
                    </Button>
                    <Button
                      variant="subtle-gray"
                      size="sm"
                      onClick={resetSettings}
                      leftIcon={<IconRestore size={16} stroke={1.5} />}
                    >
                      Obnoviť predvolené hodnoty
                    </Button>
                  </Group>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
          {!hasContent && <Toast type="fail" message="Vyberte aspoň jeden typ súborov." withCloseButton={false} />}
          {(limitsError || pathError) && (
            <Toast type="fail" message={limitsError || pathError!} withCloseButton={false} />
          )}
        </Stack>
      )}
      <Group position="right" spacing={16} pt={32}>
        <Button variant="subtle-gray" onClick={handleClose} disabled={loading}>
          Zrušiť
        </Button>
        <Button
          variant="primary"
          onClick={loading ? noop : handleExport}
          disabled={!organizationId || !!limitsError || !hasContent}
          loading={loading}
        >
          Spustiť export
        </Button>
      </Group>
    </Modal>
  );
}
